// Option chain rules shared with the Backtester (the same rules the Option Chain page uses),
// as pure functions so the Backtester can apply them to historical data.

// Strikes eligible for the 100% / 2nd / 3rd highest marks (AOC rule): resistance (CE) from
// SR_WINDOW_STRIKES strikes below spot upward, support (PE) from SR_WINDOW_STRIKES strikes
// above spot downward. Without a spot price every strike is eligible.
export const SR_WINDOW_STRIKES = 2;
export const strikesInSrWindow = (rows, spot) => {
  if (!spot || !rows?.length) return { ceRows: rows || [], peRows: rows || [] };
  const strikes = rows.map((r) => Number(r.strikePrice));
  const below = strikes.filter((k) => k < spot).sort((a, b) => b - a);
  const above = strikes.filter((k) => k > spot).sort((a, b) => a - b);
  const ceFrom = below[SR_WINDOW_STRIKES - 1] ?? below[below.length - 1] ?? -Infinity;
  const peTo = above[SR_WINDOW_STRIKES - 1] ?? above[above.length - 1] ?? Infinity;
  return {
    ceRows: rows.filter((r) => Number(r.strikePrice) >= ceFrom),
    peRows: rows.filter((r) => Number(r.strikePrice) <= peTo),
  };
};

// Highest / 2nd / 3rd value of each ranked column within the S/R window.
export const rankHighestValues = (rows, spot) => {
  const { ceRows, peRows } = strikesInSrWindow(rows, spot);
  const top3 = (list, side, field) => {
    const values = [...new Set(list.map((item) => item?.[side]?.[field] ?? 0))].sort((a, b) => b - a);
    return [values[0] || 0, values[1] || 0, values[2] || 0];
  };
  return {
    ceOIChange: top3(ceRows, 'CE', 'OpenInterestChange'),
    ceOI: top3(ceRows, 'CE', 'OpenInterest'),
    ceVolume: top3(ceRows, 'CE', 'TotalQtyTraded'),
    peOIChange: top3(peRows, 'PE', 'OpenInterestChange'),
    peOI: top3(peRows, 'PE', 'OpenInterest'),
    peVolume: top3(peRows, 'PE', 'TotalQtyTraded'),
  };
};

// Graph labels (AOC style): thousands → K, lakhs → L, crores → Cr, 2 decimals; small values as is.
export const formatCompact = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const abs = Math.abs(n);
  if (abs >= 10000000) return `${(n / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${(n / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${(n / 1000).toFixed(2)}K`;
  return `${Math.round(n * 100) / 100}`;
};

// PCR shown under each strike (PE ÷ CE), rounded to 2 decimals; never negative (AOC shows 0.0).
export const pcrUnderStrike = (pe, ce) => {
  const result = Number(pe) / Number(ce);
  if (!Number.isFinite(result)) return Number.isNaN(result) ? 0.0 : 0.0;
  if (result < 0) return '0.0';
  return Math.round(result * 100) / 100;
};

// Probable Swing High always shows R, Probable Swing Low always S. STRONG when this side's OI
// change is positive and at least double the other side's, otherwise NORMAL; blank without
// an OI-change signal.
export const swingLabel = (item, side) => {
  const ce = Number(item?.CE?.OpenInterestChange);
  const pe = Number(item?.PE?.OpenInterestChange);
  if (!Number.isFinite(ce) || !Number.isFinite(pe) || (ce === 0 && pe === 0)) return '';
  const [own, other, letter] = side === 'CE' ? [ce, pe, 'R'] : [pe, ce, 'S'];
  return `${own > 0 && own >= 2 * other ? 'STRONG' : 'NORMAL'} ${letter}`;
};

// GOLD / SILVER show every 1000 strike only (AOC).
export const DISPLAY_STEP = { GOLD: 1000, SILVER: 1000 };
export const applyDisplayStep = (rows, symbol) => {
  const step = DISPLAY_STEP[String(symbol || '').toUpperCase()];
  return step ? (rows || []).filter((r) => Number(r.strikePrice) % step === 0) : rows || [];
};

// "Select Strike" menu: strikes on each side of spot; "Max Strike" = every strike.
export const MAX_STRIKE_OPTION = 'Max Strike';
export const STRIKE_OPTIONS = ['7 Index', '10 Index', '15 Index', 'All Index', MAX_STRIKE_OPTION];
const STRIKE_RANGE = { '7 Index': 7, '10 Index': 10, '15 Index': 15, 'All Index': 20 };
export const sliceAroundSpot = (rows, spot, option) => {
  if (!option || option === MAX_STRIKE_OPTION || !rows?.length) return rows || [];
  const range = STRIKE_RANGE[option] ?? 15;
  let mid = rows.findIndex((r) => Number(r.strikePrice) >= spot);
  if (mid === -1) mid = spot ? rows.length - 1 : Math.floor(rows.length / 2);
  return rows.slice(Math.max(mid - range, 0), Math.min(mid + range, rows.length));
};

// 9:30 AM reversal values (AOC "Average S/R (9:30 AM)"): Resistance(K) = the reversal
//   L(K) = spot + PE(K) − CE(next strike up)
// averaged over 09:15–09:29 (`averageReversal` from the API, paired with the same next strike);
// else the 9:30 chain + 9:30 spot; else the given chain + spot. Support(K) = Resistance(previous
// strike). "Next strike" is the next row of `rows` (the strikes shown).
export const buildReversal930 = (rows, firstOptionChain, spotPrice) => {
  const ltp = (row, side) => Number(row?.[side]?.LastTradePrice) || 0;
  const sorted = (list) => [...(list || [])].sort((a, b) => Number(a.strikePrice) - Number(b.strikePrice));
  const pairValue = (list, refSpot, strike, nextStrike) => {
    const row = list.find((r) => Number(r.strikePrice) === strike);
    const next = list.find((r) => Number(r.strikePrice) === nextStrike);
    return row && next && refSpot && ltp(row, 'PE') && ltp(next, 'CE')
      ? refSpot + ltp(row, 'PE') - ltp(next, 'CE')
      : null;
  };
  const shown = sorted(rows);
  const firstRows = sorted(firstOptionChain?.data);
  const firstSpot = firstOptionChain?.spot?.LastTradePrice;
  const averages = firstOptionChain?.averageReversal || {};
  const averageNext = firstOptionChain?.averageReversalNext || {};
  const resistanceAt = (i) => {
    if (!shown[i] || !shown[i + 1]) return null;
    const strike = Number(shown[i].strikePrice);
    const nextStrike = Number(shown[i + 1].strikePrice);
    if (averages[strike] != null && Number(averageNext[strike]) === nextStrike) return Number(averages[strike]);
    return pairValue(firstRows, firstSpot, strike, nextStrike) ?? pairValue(shown, spotPrice, strike, nextStrike);
  };
  return { rows: shown, resistanceAt };
};

// Red strike (AOC style): the strike whose 9:30 band holds the spot, Support(K) ≤ spot ≤
// Resistance(K). From the strike at/below spot it moves up while Resistance < spot and down
// while Support > spot (never scanning the whole chain).
export const redStrikeFor = ({ rows, resistanceAt }, spotPrice) => {
  if (!spotPrice || !rows.length) return null;
  const above = rows.findIndex((row) => Number(row.strikePrice) > spotPrice);
  let i = above === -1 ? rows.length - 1 : Math.max(above - 1, 0);
  const start = i;
  const MAX_STEPS = 5;
  for (let step = 0; step < MAX_STEPS; step++) {
    const resistance = resistanceAt(i);
    const support = resistanceAt(i - 1);
    if (resistance !== null && resistance < spotPrice && i + 1 < rows.length) i += 1;
    else if (support !== null && support > spotPrice && i - 1 >= 0) i -= 1;
    else break;
    if (Math.abs(i - start) > MAX_STEPS) break;
  }
  return Number(rows[i].strikePrice);
};

// Spot Chart lines (AOC style), centred on the red strike K0: R for K0 and the two strikes
// above, S for K0 and the two below, at their 9:30 values.
export const chartLevelsFor = ({ rows, resistanceAt }, redStrike) => {
  const base = rows.findIndex((row) => Number(row.strikePrice) === redStrike);
  if (base === -1) return [];
  const levels = [];
  for (let i = base; i <= base + 2; i++) {
    const price = resistanceAt(i);
    if (price !== null) levels.push({ kind: 'R', strike: Number(rows[i].strikePrice), price });
  }
  for (let i = base; i >= base - 2 && i >= 1; i--) {
    const price = resistanceAt(i - 1);
    if (price !== null) levels.push({ kind: 'S', strike: Number(rows[i].strikePrice), price });
  }
  return levels.map((l) => ({ ...l, price: Math.round(l.price * 100) / 100 }));
};
